import { supabase } from '../utils/supabase';
import { User } from '@supabase/supabase-js';

// Types for notification settings
export interface QuietHours {
  enabled: boolean;
  start: string; // HH:MM format
  end: string;   // HH:MM format
}

export interface FrequencyPreferences {
  daily: boolean;
  weekly: boolean;
  monthly: boolean;
  quarterly: boolean;
}

export interface NotificationTypeSettings {
  documentation_reminders: boolean;
  progress_summaries: boolean;
  achievement_celebrations: boolean;
  goal_setting_prompts: boolean;
  reflection_reminders: boolean;
  self_review: boolean;
  career_opportunities: boolean;
  milestone_celebrations: boolean;
  streak_reminders: boolean;
}

export interface NotificationFrequencySettings {
  documentation_reminders: string; // 'daily', 'weekly_start', 'weekly_end', 'biweekly', 'monthly'
  progress_summaries: string; // 'weekly', 'biweekly', 'monthly'
  achievement_celebrations: string; // 'immediate', 'daily_digest', 'weekly_digest'
  goal_setting_prompts: string; // 'weekly', 'monthly', 'quarterly'
  reflection_reminders: string; // 'weekly', 'monthly'
  self_review: string; // 'monthly', 'quarterly', 'semi_annually', 'annually'
  career_opportunities: string; // 'monthly', 'quarterly', 'semi_annually', 'annually'
  milestone_celebrations: string; // 'immediate', 'weekly_digest'
  streak_reminders: string; // 'daily', 'weekly'
}

export interface ScheduledNotificationSettings {
  enabled: boolean;
  preferred_time: string; // HH:MM format
  timezone: string; // 'auto' or specific timezone
  quiet_hours: QuietHours;
  frequency_preferences: FrequencyPreferences;
  notification_types: NotificationTypeSettings;
  notification_frequencies: NotificationFrequencySettings;
}

export interface CategorySettings {
  achievements?: boolean;
  milestones?: boolean;
  reminders?: boolean;
  summaries?: boolean;
  [key: string]: boolean | undefined;
}

export interface NotificationSettings {
  bell: boolean;
  email: boolean;
  banner: boolean;
  categories: CategorySettings;
  scheduled_notifications: ScheduledNotificationSettings;
}

export interface BannerPreferences {
  enabled: boolean;
  [key: string]: any;
}

export interface NotificationSettingsRecord {
  id: string;
  user_id: string;
  settings: NotificationSettings;
  banner_preferences: BannerPreferences;
  created_at: string;
  updated_at: string;
}

export class NotificationSettingsService {
  private static instance: NotificationSettingsService;
  private cache: Map<string, { settings: NotificationSettingsRecord; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  public static getInstance(): NotificationSettingsService {
    if (!NotificationSettingsService.instance) {
      NotificationSettingsService.instance = new NotificationSettingsService();
    }
    return NotificationSettingsService.instance;
  }

  /**
   * Get notification settings for a user
   */
  async getNotificationSettings(userId: string): Promise<NotificationSettings> {
    try {
      // Check cache first
      const cached = this.cache.get(userId);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.settings.settings;
      }

      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found, create default settings
          return await this.createDefaultSettings(userId);
        }
        throw error;
      }

      // Cache the result
      this.cache.set(userId, {
        settings: data,
        timestamp: Date.now()
      });

      return data.settings;
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      // Return default settings as fallback
      return this.getDefaultSettings();
    }
  }

  /**
   * Update notification settings for a user
   */
  async updateNotificationSettings(
    userId: string, 
    updates: Partial<NotificationSettings>
  ): Promise<NotificationSettings> {
    try {
      // Get current settings
      const currentSettings = await this.getNotificationSettings(userId);
      
      // Merge updates with current settings
      const updatedSettings: NotificationSettings = {
        ...currentSettings,
        ...updates,
        // Deep merge for nested objects
        categories: {
          ...currentSettings.categories,
          ...updates.categories
        },
        scheduled_notifications: {
          ...currentSettings.scheduled_notifications,
          ...updates.scheduled_notifications,
          quiet_hours: {
            ...currentSettings.scheduled_notifications.quiet_hours,
            ...updates.scheduled_notifications?.quiet_hours
          },
          frequency_preferences: {
            ...currentSettings.scheduled_notifications.frequency_preferences,
            ...updates.scheduled_notifications?.frequency_preferences
          },
          notification_types: {
            ...currentSettings.scheduled_notifications.notification_types,
            ...updates.scheduled_notifications?.notification_types
          },
          notification_frequencies: {
            ...currentSettings.scheduled_notifications.notification_frequencies,
            ...updates.scheduled_notifications?.notification_frequencies
          }
        }
      };

      const { data, error } = await supabase
        .from('notification_settings')
        .update({ 
          settings: updatedSettings,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      // Update cache
      this.cache.set(userId, {
        settings: data,
        timestamp: Date.now()
      });

      // Update scheduled notification rules if needed
      if (updates.scheduled_notifications) {
        await this.updateScheduledNotificationRules(userId, updatedSettings.scheduled_notifications);
      }

      return data.settings;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  }

  /**
   * Update banner preferences for a user
   */
  async updateBannerPreferences(
    userId: string, 
    preferences: Partial<BannerPreferences>
  ): Promise<BannerPreferences> {
    try {
      const { data, error } = await supabase
        .from('notification_settings')
        .update({ 
          banner_preferences: preferences,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select('banner_preferences')
        .single();

      if (error) throw error;

      // Invalidate cache
      this.cache.delete(userId);

      return data.banner_preferences;
    } catch (error) {
      console.error('Error updating banner preferences:', error);
      throw error;
    }
  }

  /**
   * Check if a specific notification type is enabled for a user
   */
  async isNotificationTypeEnabled(
    userId: string, 
    notificationType: keyof NotificationTypeSettings
  ): Promise<boolean> {
    try {
      const settings = await this.getNotificationSettings(userId);
      return settings.scheduled_notifications.notification_types[notificationType] ?? false;
    } catch (error) {
      console.error('Error checking notification type:', error);
      return false;
    }
  }

  /**
   * Check if notifications are enabled for a delivery method
   */
  async isDeliveryMethodEnabled(
    userId: string, 
    deliveryMethod: 'bell' | 'email' | 'banner'
  ): Promise<boolean> {
    try {
      const settings = await this.getNotificationSettings(userId);
      return settings[deliveryMethod] ?? false;
    } catch (error) {
      console.error('Error checking delivery method:', error);
      return false;
    }
  }

  /**
   * Check if user is in quiet hours
   */
  async isInQuietHours(userId: string, timezone?: string): Promise<boolean> {
    try {
      const settings = await this.getNotificationSettings(userId);
      const quietHours = settings.scheduled_notifications.quiet_hours;
      
      if (!quietHours.enabled) return false;

      const userTimezone = timezone || settings.scheduled_notifications.timezone;
      const now = new Date();
      
      // Convert current time to user's timezone
      const currentTime = userTimezone === 'auto' 
        ? now.toLocaleTimeString('en-US', { hour12: false, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })
        : now.toLocaleTimeString('en-US', { hour12: false, timeZone: userTimezone });

      const [currentHour, currentMinute] = currentTime.split(':').map(Number);
      const currentMinutes = currentHour * 60 + currentMinute;

      const [startHour, startMinute] = quietHours.start.split(':').map(Number);
      const startMinutes = startHour * 60 + startMinute;

      const [endHour, endMinute] = quietHours.end.split(':').map(Number);
      const endMinutes = endHour * 60 + endMinute;

      // Handle overnight quiet hours (e.g., 22:00 to 08:00)
      if (startMinutes > endMinutes) {
        return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
      } else {
        return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      }
    } catch (error) {
      console.error('Error checking quiet hours:', error);
      return false;
    }
  }

  /**
   * Get default notification settings
   */
  private getDefaultSettings(): NotificationSettings {
    return {
      bell: true,
      email: false,
      banner: true,
      categories: {
        achievements: true,
        milestones: true,
        reminders: false,
        summaries: false
      },
      scheduled_notifications: {
        enabled: true,
        preferred_time: "18:00",
        timezone: "auto",
        quiet_hours: {
          enabled: false,
          start: "22:00",
          end: "08:00"
        },
        frequency_preferences: {
          daily: false,
          weekly: true,
          monthly: false,
          quarterly: false
        },
        notification_types: {
          documentation_reminders: false,
          progress_summaries: true,
          achievement_celebrations: true,
          goal_setting_prompts: false,
          reflection_reminders: false,
          self_review: true,
          career_opportunities: false,
          milestone_celebrations: true,
          streak_reminders: false
        },
        notification_frequencies: {
          documentation_reminders: 'daily',
          progress_summaries: 'weekly',
          achievement_celebrations: 'immediate',
          goal_setting_prompts: 'weekly',
          reflection_reminders: 'weekly',
          self_review: 'quarterly',
          career_opportunities: 'quarterly',
          milestone_celebrations: 'immediate',
          streak_reminders: 'daily'
        }
      }
    };
  }

  /**
   * Create default settings for a new user
   */
  private async createDefaultSettings(userId: string): Promise<NotificationSettings> {
    try {
      const defaultSettings = this.getDefaultSettings();
      
      const { data, error } = await supabase
        .from('notification_settings')
        .insert({
          user_id: userId,
          settings: defaultSettings,
          banner_preferences: { enabled: true }
        })
        .select()
        .single();

      if (error) throw error;

      // Cache the result
      this.cache.set(userId, {
        settings: data,
        timestamp: Date.now()
      });

      return data.settings;
    } catch (error) {
      console.error('Error creating default settings:', error);
      return this.getDefaultSettings();
    }
  }

  /**
   * Update scheduled notification rules based on settings changes
   */
  private async updateScheduledNotificationRules(
    userId: string, 
    scheduledSettings: ScheduledNotificationSettings
  ): Promise<void> {
    try {
      if (!scheduledSettings.enabled) {
        // Disable all scheduled notification rules for this user
        await supabase
          .from('scheduled_notification_rules')
          .update({ active: false })
          .eq('user_id', userId);
        return;
      }

      // Update or create scheduled notification rules based on preferences
      const enabledTypes = Object.entries(scheduledSettings.notification_types)
        .filter(([_, enabled]) => enabled)
        .map(([type, _]) => type);

      // This is a simplified implementation - in a full implementation,
      // you would create/update specific scheduled notification rules
      // based on the user's preferences and the available notification templates
      
      console.log(`Updated scheduled notification preferences for user ${userId}:`, {
        enabledTypes,
        preferredTime: scheduledSettings.preferred_time,
        timezone: scheduledSettings.timezone,
      });
    } catch (error) {
      console.error('Error updating scheduled notification rules:', error);
    }
  }

  /**
   * Clear cache for a user
   */
  clearCache(userId?: string): void {
    if (userId) {
      this.cache.delete(userId);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }
}

// Export singleton instance
export const notificationSettingsService = NotificationSettingsService.getInstance(); 